import "@fontsource-variable/manrope";
import "./styles.css";
import { validateSubmission } from "./contact-schema";
import { contactFields } from "./form-fields";
import { securityConfig } from "./security-config";
import { siteContent } from "./site-content";
import type { ContactField, ContactFieldValue, Testimonial } from "./types";

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId?: string) => void;
    };
  }
}

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("Application root was not found.");

document.title = siteContent.pageTitle;

const escapeHtml = (value: string) =>
  value.replace(
    /[&<>'"]/g,
    (character) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#039;", '"': "&quot;" })[character] ??
      character,
  );

const icon = (name: "arrow" | "send" | "close" | "check") => {
  const icons = {
    arrow: '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h12M11 5l5 5-5 5"/></svg>',
    send: '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="m3 10 14-6-5.5 12-2.2-4.4L3 10Zm6.3 1.6L17 4"/></svg>',
    close: '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15"/></svg>',
    check: '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="m4 10 4 4 8-8"/></svg>',
  };
  return icons[name];
};

function renderWordmark(className: string) {
  const { brand } = siteContent;
  const mark =
    brand.mark.kind === "image"
      ? `<img src="${escapeHtml(brand.mark.src)}" alt="${escapeHtml(brand.mark.alt)}">`
      : escapeHtml(brand.mark.text);

  return `<a class="wordmark ${className}" href="${escapeHtml(brand.href)}" aria-label="${escapeHtml(`${brand.name} contact page`)}">
    <span class="wordmark-mark${brand.mark.kind === "image" ? " wordmark-mark--image" : ""}">${mark}</span>
    <span>${escapeHtml(brand.name)}</span>
  </a>`;
}

function renderTestimonial(testimonial: Testimonial) {
  const content = `
    <article class="testimonial-card">
      <p>“${escapeHtml(testimonial.quote)}”</p>
      <footer>
        ${
          testimonial.avatar
            ? `<img src="${escapeHtml(testimonial.avatar)}" alt="" loading="lazy">`
            : `<span class="testimonial-avatar" aria-hidden="true">${escapeHtml(testimonial.clientName.charAt(0))}</span>`
        }
        <span>
          <strong>${escapeHtml(testimonial.clientName)}</strong>
          ${
            testimonial.role || testimonial.company
              ? `<small>${escapeHtml([testimonial.role, testimonial.company].filter(Boolean).join(" · "))}</small>`
              : ""
          }
        </span>
      </footer>
    </article>`;

  return testimonial.url
    ? `<a class="testimonial-link" href="${escapeHtml(testimonial.url)}" target="_blank" rel="noreferrer">${content}</a>`
    : content;
}

function commonFieldAttributes(field: ContactField) {
  return [
    `id="field-${escapeHtml(field.key)}"`,
    `name="${escapeHtml(field.key)}"`,
    field.required ? "required" : "",
    field.placeholder ? `placeholder="${escapeHtml(field.placeholder)}"` : "",
    field.autocomplete ? `autocomplete="${escapeHtml(field.autocomplete)}"` : "",
    field.minLength ? `minlength="${field.minLength}"` : "",
    field.maxLength ? `maxlength="${field.maxLength}"` : "",
    `aria-describedby="error-${escapeHtml(field.key)}"`,
  ]
    .filter(Boolean)
    .join(" ");
}

function renderField(field: ContactField) {
  const label = `${escapeHtml(field.label)}${field.required ? '<span aria-hidden="true">*</span>' : ""}`;
  let control = "";

  if (field.type === "textarea") {
    control = `<textarea ${commonFieldAttributes(field)} rows="4"></textarea>`;
  } else if (field.type === "select") {
    control = `<select ${commonFieldAttributes(field)}>
      <option value="">${escapeHtml(field.emptyOptionLabel ?? "Select an option")}</option>
      ${(field.options ?? []).map((option) => `<option value="${escapeHtml(option)}">${escapeHtml(option)}</option>`).join("")}
    </select>`;
  } else if (field.type === "radio" || field.type === "checkbox") {
    control = `<fieldset class="choice-field" aria-describedby="error-${escapeHtml(field.key)}">
      <legend>${label}</legend>
      <div class="choice-list">
        ${(field.options ?? [])
          .map(
            (option, index) => `<label>
              <input type="${field.type}" name="${escapeHtml(field.key)}" value="${escapeHtml(option)}" ${field.type === "radio" && field.required && index === 0 ? "required" : ""}>
              <span>${escapeHtml(option)}</span>
            </label>`,
          )
          .join("")}
      </div>
    </fieldset>`;
  } else {
    control = `<input type="${field.type}" ${commonFieldAttributes(field)}>`;
  }

  if (field.type === "radio" || field.type === "checkbox") {
    return `<div class="form-field form-field--choices">${control}<p class="field-error" id="error-${escapeHtml(field.key)}"></p></div>`;
  }

  return `<div class="form-field">
    <label for="field-${escapeHtml(field.key)}">${label}</label>
    ${control}
    <p class="field-error" id="error-${escapeHtml(field.key)}"></p>
  </div>`;
}

const testimonialCards = siteContent.testimonials.map(renderTestimonial).join("");
const privacyParagraphs = siteContent.privacy.paragraphs
  .map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`)
  .join("");

app.innerHTML = `
  <main class="page-frame">
    <div class="media-stage" role="img" aria-label="${escapeHtml(siteContent.showreel.posterAlt)}">
      <img class="media-poster" src="${escapeHtml(siteContent.showreel.poster)}" alt="">
      <video class="hero-video hero-video--a" muted playsinline preload="metadata" poster="${escapeHtml(siteContent.showreel.poster)}"></video>
      <video class="hero-video hero-video--b" muted playsinline preload="metadata" poster="${escapeHtml(siteContent.showreel.poster)}"></video>
      <div class="media-scrim" aria-hidden="true"></div>
    </div>

    <header class="site-header">
      <div class="selected-work">
        <span>${escapeHtml(siteContent.header.mediaLabel)}</span>
        <span>${escapeHtml(siteContent.header.mediaYear)}</span>
      </div>
      ${renderWordmark("site-wordmark")}
      <span class="availability"><i aria-hidden="true"></i> ${escapeHtml(siteContent.header.availabilityText)}</span>
    </header>

    <section class="testimonials" aria-label="Client testimonials">
      <div class="testimonial-heading">
        <span>${escapeHtml(siteContent.testimonialsLabel)}</span>
      </div>
      <div class="testimonial-viewport">
        <div class="testimonial-belt">
          <div class="testimonial-group">${testimonialCards}</div>
          <div class="testimonial-group" aria-hidden="true" inert>${testimonialCards}</div>
        </div>
      </div>
    </section>

    <section class="form-panel" aria-labelledby="contact-heading">
      <div class="form-content">
        <header class="form-intro">
          ${renderWordmark("panel-wordmark")}
          <p class="eyebrow">${escapeHtml(siteContent.contact.eyebrow)}</p>
          <h1 id="contact-heading">${escapeHtml(siteContent.contact.headline)}</h1>
          <p class="intro-copy">${escapeHtml(siteContent.contact.introduction)}</p>
        </header>

        <div class="success-panel" id="success-panel" hidden tabindex="-1">
          <span class="success-icon">${icon("check")}</span>
          <p class="eyebrow">${escapeHtml(siteContent.success.eyebrow)}</p>
          <h2>${escapeHtml(siteContent.success.title)}</h2>
          <p>${escapeHtml(siteContent.success.message)}</p>
          <button class="text-button" id="send-another" type="button">${escapeHtml(siteContent.success.resetLabel)} ${icon("arrow")}</button>
        </div>

        <form id="contact-form" novalidate>
          <div class="fields-grid">${contactFields.map(renderField).join("")}</div>

          <div class="honeypot" aria-hidden="true">
            <label for="website">Website</label>
            <input id="website" name="website" type="text" tabindex="-1" autocomplete="off">
          </div>

          <div id="turnstile-slot" class="turnstile-slot"></div>
          <p id="form-status" class="form-status" role="status" aria-live="polite"></p>

          <button class="submit-button" id="submit-button" type="submit">
            <span>${escapeHtml(siteContent.contact.submitLabel)}</span>
            <span class="submit-icon">${icon("send")}</span>
          </button>
        </form>
      </div>

      <footer class="form-footer">
        <span>© ${new Date().getFullYear()} ${escapeHtml(siteContent.brand.name)}</span>
        <p class="privacy-note">
          ${escapeHtml(siteContent.privacy.summary)}
          <a href="${escapeHtml(siteContent.privacy.url)}" id="privacy-link">${escapeHtml(siteContent.privacy.linkLabel)}</a>
        </p>
      </footer>
    </section>
  </main>

  <dialog class="privacy-dialog" id="privacy-dialog" aria-labelledby="privacy-title">
    <button class="dialog-close" type="button" aria-label="Close ${escapeHtml(siteContent.privacy.linkLabel.toLowerCase())}">${icon("close")}</button>
    <p class="eyebrow">${escapeHtml(siteContent.privacy.eyebrow)}</p>
    <h2 id="privacy-title">${escapeHtml(siteContent.privacy.title)}</h2>
    ${privacyParagraphs}
  </dialog>
`;

function getFieldValue(form: HTMLFormElement, field: ContactField): ContactFieldValue {
  const formData = new FormData(form);
  return field.type === "checkbox"
    ? formData.getAll(field.key).map(String)
    : String(formData.get(field.key) ?? "");
}

function setFieldError(key: string, message = "") {
  const error = document.querySelector<HTMLElement>(`#error-${CSS.escape(key)}`);
  if (error) error.textContent = message;
  const controls = document.querySelectorAll<HTMLElement>(`[name="${CSS.escape(key)}"]`);
  controls.forEach((control) => {
    if (message) control.setAttribute("aria-invalid", "true");
    else control.removeAttribute("aria-invalid");
  });
}

function setupForm() {
  const form = document.querySelector<HTMLFormElement>("#contact-form");
  const submitButton = document.querySelector<HTMLButtonElement>("#submit-button");
  const formStatus = document.querySelector<HTMLElement>("#form-status");
  const successPanel = document.querySelector<HTMLElement>("#success-panel");
  const formIntro = document.querySelector<HTMLElement>(".form-intro");
  const sendAnother = document.querySelector<HTMLButtonElement>("#send-another");
  if (!form || !submitButton || !formStatus || !successPanel || !formIntro || !sendAnother) return;

  let turnstileToken = securityConfig.turnstileEnabled ? "" : "local-development";
  let widgetId: string | undefined;
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  const isLocal = ["localhost", "127.0.0.1"].includes(window.location.hostname);

  const initializeTurnstile = () => {
    const slot = document.querySelector<HTMLElement>("#turnstile-slot");
    if (!slot) return;

    if (!securityConfig.turnstileEnabled) {
      slot.hidden = true;
      return;
    }

    if (!turnstileSiteKey) {
      if (isLocal) {
        turnstileToken = "local-development";
        slot.hidden = true;
      } else {
        slot.innerHTML = '<span class="setup-warning">Security verification is not configured yet.</span>';
        submitButton.disabled = true;
      }
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => {
      if (!window.turnstile) return;
      widgetId = window.turnstile.render(slot, {
        sitekey: turnstileSiteKey,
        theme: "dark",
        size: "flexible",
        appearance: "interaction-only",
        callback: (token: string) => {
          turnstileToken = token;
          formStatus.textContent = "";
        },
        "expired-callback": () => {
          turnstileToken = "";
        },
        "error-callback": () => {
          turnstileToken = "";
          formStatus.textContent = "The security check could not load. Please try again.";
        },
      });
    });
    document.head.append(script);
  };

  initializeTurnstile();

  contactFields.forEach((field) => {
    const controls = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      `[name="${CSS.escape(field.key)}"]`,
    );
    controls.forEach((control) => {
      control.addEventListener("input", () => setFieldError(field.key));
      control.addEventListener("blur", () => {
        if (!control.validity.valid) setFieldError(field.key, control.validationMessage);
      });
    });
  });

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    formStatus.textContent = "";
    contactFields.forEach((field) => setFieldError(field.key));

    const payload = {
      submissionId: crypto.randomUUID(),
      fields: Object.fromEntries(contactFields.map((field) => [field.key, getFieldValue(form, field)])),
      turnstileToken,
      website: String(new FormData(form).get("website") ?? ""),
    };
    const validation = validateSubmission(payload, isLocal || !securityConfig.turnstileEnabled);

    if (!validation.ok) {
      for (const [key, message] of Object.entries(validation.fieldErrors)) setFieldError(key, message);
      formStatus.textContent = validation.message;
      form.querySelector<HTMLElement>("[aria-invalid='true']")?.focus();
      return;
    }

    submitButton.disabled = true;
    submitButton.classList.add("is-loading");
    submitButton.querySelector("span")!.textContent = siteContent.contact.sendingLabel;

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(validation.value),
      });
      const result = (await response.json()) as {
        ok: boolean;
        message?: string;
        fieldErrors?: Record<string, string>;
      };

      if (!response.ok || !result.ok) {
        if (result.fieldErrors) {
          for (const [key, message] of Object.entries(result.fieldErrors)) setFieldError(key, message);
        }
        throw new Error(result.message || "Your inquiry could not be sent.");
      }

      form.hidden = true;
      formIntro.hidden = true;
      successPanel.hidden = false;
      successPanel.focus();
      form.reset();
      turnstileToken = isLocal || !securityConfig.turnstileEnabled ? "local-development" : "";
    } catch (error) {
      formStatus.textContent = error instanceof Error ? error.message : "Your inquiry could not be sent.";
      if (siteContent.fallbackForm.url) {
        formStatus.innerHTML += ` <a href="${escapeHtml(siteContent.fallbackForm.url)}" target="_blank" rel="noreferrer">${escapeHtml(siteContent.fallbackForm.label)}</a>`;
      }
    } finally {
      submitButton.disabled = false;
      submitButton.classList.remove("is-loading");
      submitButton.querySelector("span")!.textContent = siteContent.contact.submitLabel;
      if (securityConfig.turnstileEnabled && window.turnstile && widgetId) {
        window.turnstile.reset(widgetId);
      }
    }
  });

  sendAnother.addEventListener("click", () => {
    successPanel.hidden = true;
    formIntro.hidden = false;
    form.hidden = false;
    form.querySelector<HTMLElement>("input, textarea, select")?.focus();
  });
}

function setupMediaPlaylist() {
  const stage = document.querySelector<HTMLElement>(".media-stage");
  const videos = Array.from(document.querySelectorAll<HTMLVideoElement>(".hero-video"));
  const { showreel } = siteContent;
  if (!stage || videos.length !== 2 || showreel.clips.length === 0) return;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const useMobileSources = window.matchMedia("(max-width: 820px)").matches;
  const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true;
  let activeSlot = 0;
  let playlistIndex = showreel.randomizeStart ? Math.floor(Math.random() * showreel.clips.length) : 0;
  let sourcesLoaded = false;
  let stageVisible = true;
  let failedClips = 0;
  let isAdvancing = false;

  const activeVideo = () => videos[activeSlot]!;

  const pauseAll = () => videos.forEach((video) => video.pause());

  const loadClip = async (slot: number, index: number) => {
    const video = videos[slot]!;
    const clip = showreel.clips[index]!;
    const sources = useMobileSources && clip.mobile ? clip.mobile : clip.desktop;
    const sourceElements = [
      sources.webm ? Object.assign(document.createElement("source"), { src: sources.webm, type: "video/webm" }) : null,
      Object.assign(document.createElement("source"), { src: sources.mp4, type: "video/mp4" }),
    ].filter((source): source is HTMLSourceElement => source !== null);

    video.pause();
    video.removeAttribute("src");
    video.replaceChildren(...sourceElements);
    video.setAttribute("aria-label", clip.alt);
    video.load();
    await video.play();
  };

  const startPlaylist = async () => {
    if (sourcesLoaded || reduceMotion || saveData) return;
    sourcesLoaded = true;
    try {
      await loadClip(activeSlot, playlistIndex);
      videos[activeSlot]!.classList.add("is-active");
      stage.classList.add("has-video");
      failedClips = 0;
    } catch {
      sourcesLoaded = false;
    }
  };

  const advance = async () => {
    if (isAdvancing || document.hidden || !stageVisible) return;
    isAdvancing = true;
    const nextSlot = activeSlot === 0 ? 1 : 0;
    playlistIndex = (playlistIndex + 1) % showreel.clips.length;
    try {
      await loadClip(nextSlot, playlistIndex);
      const previous = videos[activeSlot]!;
      videos[nextSlot]!.classList.add("is-active");
      previous.classList.remove("is-active");
      window.setTimeout(() => previous.pause(), showreel.crossfadeMs);
      activeSlot = nextSlot;
      failedClips = 0;
    } catch {
      failedClips += 1;
      isAdvancing = false;
      if (failedClips < showreel.clips.length) void advance();
      else {
        pauseAll();
        stage.classList.remove("has-video");
      }
      return;
    }
    isAdvancing = false;
  };

  videos.forEach((video) => {
    video.addEventListener("ended", () => {
      if (video === activeVideo()) void advance();
    });
    video.addEventListener("error", () => {
      if (video === activeVideo()) void advance();
    });
  });

  const observer = new IntersectionObserver(
    ([entry]) => {
      stageVisible = Boolean(entry?.isIntersecting);
      if (!stageVisible) pauseAll();
      else if (sourcesLoaded && !document.hidden) void activeVideo().play().catch(() => undefined);
    },
    { threshold: 0.15 },
  );
  observer.observe(stage);

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) pauseAll();
    else if (sourcesLoaded && stageVisible) void activeVideo().play().catch(() => undefined);
  });

  if (!reduceMotion && !saveData) void startPlaylist();
}

function setupPrivacyDialog() {
  const dialog = document.querySelector<HTMLDialogElement>("#privacy-dialog");
  const link = document.querySelector<HTMLAnchorElement>("#privacy-link");
  const close = dialog?.querySelector<HTMLButtonElement>(".dialog-close");
  if (!dialog || !link || !close || siteContent.privacy.url !== "#privacy") return;

  link.addEventListener("click", (event) => {
    event.preventDefault();
    dialog.showModal();
  });
  close.addEventListener("click", () => dialog.close());
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });
}

setupForm();
setupMediaPlaylist();
setupPrivacyDialog();
